import { useRef, useState } from 'react';

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { reportingLeLensCardDataPath, reportingLeLensDataPath } from '~/consts/reportingLeLensApi';

import type { LensCardData, LensFilterValues, LensResolvedPeriod } from '~/types/leLens';

import {
  canonicalRequestedCardIds,
  lensDataCacheOptions,
  lensDataIncludesCard,
  lensDataRequestIdentity,
} from '~/utils/leLensDataRequest';
import { expandLensCardResponse, expandLensDataResponse } from '~/utils/leLensTransport';
import { loadLensDataStream } from '~/utils/loadLensDataStream';
import { requestWithTimeout } from '~/utils/requestWithTimeout';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { reportingLeLensQueryKeys } from './useGetReportingLeLens';

type LensDataResponse = {
  cards?: Array<LensCardData>;
  filterOptions?: Record<string, Array<string>>;
  period?: LensResolvedPeriod;
  lensVersion?: number;
  viewRevision?: number;
  requestedCardIds?: ReadonlyArray<string>;
};

/**
 * How long a board's cards may take before the wait itself is the answer.
 *
 * There is no server-side deadline on a card query, so a backend that accepts
 * the request and never answers left every card in its loading state for as
 * long as the tab stayed open, with no error, no retry and nothing to read.
 * Well above a real BigQuery round trip for the seeded boards (the slowest
 * observed is a few seconds) and far below a reader's patience, so a genuine
 * query is never cut short and a hang always resolves into something with a
 * recovery action on it.
 */
const LENS_DATA_TIMEOUT_MS = 60_000;
const LENS_DATA_TIMEOUT_MESSAGE =
  'This Lens’s numbers are taking longer than expected. Nothing was changed, so try again.';

/**
 * Every card on the board comes back from one request, so the filters are part
 * of the key. includeFilterOptions is a second axis on the same data, which is
 * why a writer has to match on the prefix rather than reconstruct the key.
 */
export const lensDataQueryKeyPrefix = (lensId: string, filters: LensFilterValues) =>
  [...reportingLeLensQueryKeys.detail(lensId), 'data', filters] as const;

export function useGetReportingLeLensData(
  lensId: string | undefined,
  filters: LensFilterValues = {},
  includeFilterOptions = true,
  lensVersion?: number,
  viewRevision?: number,
  _openId?: string,
  requestedCardIds?: ReadonlyArray<string>,
) {
  const forceRefresh = useRef(false);
  const requestGeneration = useRef(0);
  // Use the cache's canonical object ordering: equivalent filters have one identity.
  const cardIds = canonicalRequestedCardIds(requestedCardIds);
  const identity = lensDataRequestIdentity(lensId, filters, includeFilterOptions, lensVersion, viewRevision, cardIds);
  const [partial, setPartial] = useState<{ identity: string; data: ReturnType<typeof expandLensDataResponse> }>();
  const query = useQuery({
    queryKey: [
      ...lensDataQueryKeyPrefix(lensId ?? '', filters),
      lensDataCacheOptions(includeFilterOptions, lensVersion, viewRevision, cardIds),
    ],
    queryFn: async ({ signal }) => {
      const refresh = forceRefresh.current;
      forceRefresh.current = false;
      const generation = ++requestGeneration.current;
      setPartial(undefined);
      const response = await loadLensDataStream(
        resolveReportingLeLensUrl(reportingLeLensDataPath(lensId)),
        {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filters, includeFilterOptions, refresh, stream: true, requestedCardIds: cardIds }),
          signal,
        },
        (data) => {
          if (!signal.aborted && generation === requestGeneration.current) setPartial({ identity, data });
        },
        LENS_DATA_TIMEOUT_MS,
        LENS_DATA_TIMEOUT_MESSAGE,
      );
      return { ...response, lensId, requestIdentity: identity, requestedCardIds: cardIds };
    },
    enabled: Boolean(lensId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    // A board fetch is a BigQuery bill. Retrying it silently spends that
    // again and delays the moment the reader is told anything, so a failure
    // is reported once and retried only when a person asks.
    retry: false,
    // A filter change is a new key with nothing in it, so without this the whole
    // board drops to its loading state for the length of a BigQuery round trip
    // and every card the filter did not even touch flashes empty.
    placeholderData: keepPreviousData,
  });

  // `isFetching` alone also covers an ordinary refresh of the result already
  // selected by this key. The page must not veil that fresh result: it needs
  // to distinguish it from the one case where the visible rows belong to the
  // previous filters or reporting period. TanStack marks that hand-off as
  // placeholder data, and it remains meaningful only while the replacement
  // request is in flight.
  const streamed = partial?.identity === identity && (query.isFetching || query.isError) ? partial.data : undefined;
  const retained =
    streamed &&
    !query.isPlaceholderData &&
    query.data?.requestIdentity === identity &&
    query.data?.lensVersion === streamed.lensVersion &&
    query.data?.viewRevision === streamed.viewRevision
      ? query.data
      : undefined;
  const streamedData = streamed
    ? {
        ...streamed,
        lensId,
        filterOptions: retained?.filterOptions ?? streamed.filterOptions,
        cards: [
          ...new Map([...(retained?.cards ?? []), ...streamed.cards].map((card) => [card.cardId, card])).values(),
        ],
      }
    : undefined;
  return {
    ...query,
    ...(streamedData ? { data: streamedData, isPlaceholderData: false } : {}),
    requestIdentity: identity,
    // Partial streams never gain terminal-response identity, even after failure.
    responseIdentity: streamedData ? undefined : query.data?.requestIdentity,
    refreshData: () => {
      forceRefresh.current = true;
      return query.refetch();
    },
    isShowingPreviousData: query.isPlaceholderData && query.isFetching && partial?.identity !== identity,
  };
}

export const refreshReportingLeLensCardMutationKey = [...reportingLeLensQueryKeys.all, 'refresh-card'] as const;

export function useRefreshReportingLeLensCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: refreshReportingLeLensCardMutationKey,
    mutationFn: async ({ lensId, cardId, filters }: { lensId: string; cardId: string; filters?: LensFilterValues }) => {
      const response = await requestWithTimeout<
        Parameters<typeof expandLensCardResponse>[0] & Pick<LensDataResponse, 'lensVersion' | 'viewRevision'>
      >(
        resolveReportingLeLensUrl(reportingLeLensCardDataPath(lensId, cardId)),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filters: filters ?? {} }),
        },
        'This card is taking longer than expected. Nothing was changed, so try again.',
        LENS_DATA_TIMEOUT_MS,
      );
      return {
        card: expandLensCardResponse(response).card,
        lensVersion: response.lensVersion,
        viewRevision: response.viewRevision,
      };
    },
    onSuccess: ({ card, lensVersion, viewRevision }, { lensId, filters = {} }) => {
      // setQueriesData, not setQueryData: the board's key carries a trailing
      // includeFilterOptions flag this mutation is not told about, so writing a
      // reconstructed key lands on a cache entry nothing reads and the refresh
      // costs a query and changes nothing on screen.
      queryClient.setQueriesData<LensDataResponse>(
        { queryKey: lensDataQueryKeyPrefix(lensId, filters) },
        (current = {}) => {
          if (current.lensVersion !== lensVersion || current.viewRevision !== viewRevision) {
            return undefined;
          }
          if (!lensDataIncludesCard(current.requestedCardIds, card.cardId)) return current;
          const cards = current.cards ?? [];
          const index = cards.findIndex((item) => item.cardId === card.cardId);
          return {
            ...current,
            cards: index === -1 ? [...cards, card] : cards.map((item) => (item.cardId === card.cardId ? card : item)),
          };
        },
      );
      // Refresh is the button people press when a card looks wrong, so re-read
      // the definition too: new rows against a stale spec still looks wrong.
      queryClient.refetchQueries({ exact: true, queryKey: reportingLeLensQueryKeys.detail(lensId) });
    },
    retry: false,
  });
}
